import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { doc, collection, addDoc, deleteDoc, updateDoc, increment, serverTimestamp, onSnapshot, query, orderBy, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/authContext';

const StoryCard = ({ story }) => {
  const { currentUser, userData } = useAuth();
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  const [showAllComments, setShowAllComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  
  // Interaction states
  const [userHasLiked, setUserHasLiked] = useState(false);
  const [userHasPrayed, setUserHasPrayed] = useState(false);
  const [likeCount, setLikeCount] = useState(story.likeCount || 0);
  const [prayerCount, setPrayerCount] = useState(story.prayerCount || 0);
  const [comments, setComments] = useState([]);
  
  // Helper for avatar initials
  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  // Helper to get user's display name
  const getUserDisplayName = () => {
    // If the current user is the author of this story, use the story's author name
    if (currentUser?.uid === story.authorId) {
      return story.authorName || 'Anonymous';
    }
    // Otherwise use the current user's data
    return userData?.name || currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Anonymous';
  };

  // Helper to truncate text for preview
  const truncateText = (text, maxLines = 3) => {
    if (!text) return '';
    const lines = text.split('\n');
    if (lines.length <= maxLines) return text;
    return lines.slice(0, maxLines).join('\n') + '...';
  };

  // Helper to check if text needs truncation
  const needsTruncation = (text) => {
    if (!text) return false;
    const lines = text.split('\n');
    return lines.length > 3;
  };

  // Helper to get metrics display
  const getMetricsDisplay = () => {
    const metrics = [];
    // New metrics format (from mobile app)
    if (story.spiritualDiscussions > 0) {
      metrics.push(`${story.spiritualDiscussions} Spiritual Discussion${story.spiritualDiscussions > 1 ? 's' : ''}`);
    }
    if (story.heardGospel > 0) {
      metrics.push(`${story.heardGospel} Heard Gospel`);
    }
    if (story.experiencedLove > 0) {
      metrics.push(`${story.experiencedLove} Experienced Love`);
    }
    if (story.believersEncouraged > 0) {
      metrics.push(`${story.believersEncouraged} Believer${story.believersEncouraged > 1 ? 's' : ''} Encouraged`);
    }
    if (story.newFollowers > 0) {
      metrics.push(`${story.newFollowers} New Follower${story.newFollowers > 1 ? 's' : ''}`);
    }
    // Legacy metrics (if present)
    if (story.livesChanged > 0) {
      metrics.push(`${story.livesChanged} Live${story.livesChanged > 1 ? 's' : ''} Changed`);
    }
    if (story.soulsSaved > 0) {
      metrics.push(`${story.soulsSaved} Soul${story.soulsSaved > 1 ? 's' : ''} Saved`);
    }
    if (story.disciplesMade > 0) {
      metrics.push(`${story.disciplesMade} Disciple${story.disciplesMade > 1 ? 's' : ''} Made`);
    }
    if (story.churchesPlanted > 0) {
      metrics.push(`${story.churchesPlanted} Church${story.churchesPlanted > 1 ? 'es' : ''} Planted`);
    }
    return metrics;
  };

  // Helper to get visible comments
  const getVisibleComments = () => {
    if (!comments || comments.length === 0) return [];
    return showAllComments ? comments : comments.slice(0, 3);
  };

  // Set up real-time listeners for interactions
  useEffect(() => {
    if (!story.id || !story.authorId || !currentUser) return;

    const storyRef = doc(db, 'users', story.authorId, 'stories', story.id);

    // Listen for likes
    const likesRef = collection(storyRef, 'likes');
    const unsubscribeLikes = onSnapshot(likesRef, (snapshot) => {
      setLikeCount(snapshot.size);
      setUserHasLiked(snapshot.docs.some(doc => doc.id === currentUser.uid));
    });

    // Listen for prayers
    const prayersRef = collection(storyRef, 'prayers');
    const unsubscribePrayers = onSnapshot(prayersRef, (snapshot) => {
      setPrayerCount(snapshot.size);
      setUserHasPrayed(snapshot.docs.some(doc => doc.id === currentUser.uid));
    });

    // Listen for comments
    const commentsRef = collection(storyRef, 'comments');
    const commentsQuery = query(commentsRef, orderBy('createdAt', 'asc'));
    const unsubscribeComments = onSnapshot(commentsQuery, (snapshot) => {
      const commentsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setComments(commentsData);
    });

    return () => {
      unsubscribeLikes();
      unsubscribePrayers();
      unsubscribeComments();
    };
  }, [story.id, story.authorId, currentUser]);

  // Helper to test video URL accessibility
  const testVideoURL = (url) => {
    if (!url) return;
    
    // Test if URL is accessible
    fetch(url, { method: 'HEAD' })
      .then(response => {
        console.log('[StoryCard] Video URL test result:', {
          url: url,
          status: response.status,
          contentType: response.headers.get('content-type'),
          contentLength: response.headers.get('content-length')
        });
        
        // If URL is not accessible, show error immediately
        if (!response.ok) {
          console.error('[StoryCard] Video URL not accessible:', response.status);
          const errorOverlay = document.querySelector('.error-overlay');
          const loadingOverlay = document.querySelector('.loading-overlay');
          if (loadingOverlay) loadingOverlay.style.display = 'none';
          if (errorOverlay) errorOverlay.style.display = 'flex';
        }
      })
      .catch(error => {
        console.error('[StoryCard] Video URL test failed:', {
          url: url,
          error: error.message
        });
      });
  };

  // Test video URL when component mounts
  useEffect(() => {
    if (story.playableURL) {
      testVideoURL(story.playableURL);
    }
  }, [story.playableURL]);

  // Handle like action
  const handleLike = async () => {
    if (!currentUser) {
      alert('Please log in to like stories.');
      return;
    }

    try {
      const storyRef = doc(db, 'users', story.authorId, 'stories', story.id);
      const likeRef = doc(storyRef, 'likes', currentUser.uid);

      if (userHasLiked) {
        await deleteDoc(likeRef);
      } else {
        await setDoc(likeRef, {
          userId: currentUser.uid,
          userName: getUserDisplayName(),
          userPhotoURL: currentUser.photoURL,
          createdAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  // Handle prayer action
  const handlePray = async () => {
    if (!currentUser) {
      alert('Please log in to pray for stories.');
      return;
    }

    try {
      const storyRef = doc(db, 'users', story.authorId, 'stories', story.id);
      const prayerRef = doc(storyRef, 'prayers', currentUser.uid);

      if (userHasPrayed) {
        await deleteDoc(prayerRef);
      } else {
        await setDoc(prayerRef, {
          userId: currentUser.uid,
          userName: getUserDisplayName(),
          userPhotoURL: currentUser.photoURL,
          createdAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error toggling prayer:', error);
    }
  };

  // Handle comment submission
  const handleComment = async () => {
    if (!currentUser) {
      alert('Please log in to comment.');
      return;
    }

    if (!commentText.trim()) {
      alert('Please enter a comment.');
      return;
    }

    setIsSubmittingComment(true);
    try {
      const storyRef = doc(db, 'users', story.authorId, 'stories', story.id);
      const commentsRef = collection(storyRef, 'comments');
      
      await addDoc(commentsRef, {
        text: commentText.trim(),
        userId: currentUser.uid,
        userName: getUserDisplayName(),
        userPhotoURL: currentUser.photoURL,
        createdAt: serverTimestamp()
      });

      setCommentText('');
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const metrics = getMetricsDisplay();
  const visibleComments = getVisibleComments();

  return (
    <div className="w-full bg-white border border-gray-200 rounded-xl shadow-lg p-4 mb-4">
      {/* User info */}
      <div className="flex items-center mb-3">
        {story.authorPhotoURL ? (
          <img
            src={story.authorPhotoURL}
            alt={story.authorName || 'User'}
            className="h-10 w-10 rounded-full object-cover border-2 border-white shadow"
          />
        ) : (
          <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center text-lg font-bold text-white">
            {getInitials(story.authorName)}
          </div>
        )}
        <div className="ml-3">
          <div className="font-semibold text-gray-900">{story.authorName || 'Anonymous'}</div>
          <div className="text-xs text-gray-500">
            {story.timestamp ? new Date(story.timestamp.toDate()).toLocaleDateString() : 'Recently'}
          </div>
        </div>
      </div>

      {/* Story text (always show if present) */}
      {story.text && (
        <div className="mb-3 text-gray-700 text-sm">
          <div 
            className={`whitespace-pre-wrap ${!isTextExpanded ? 'line-clamp-3' : ''}`}
            style={!isTextExpanded ? { 
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: '1.4'
            } : {}}
          >
            {story.text}
          </div>
          {story.text.length > 150 && (
            <button
              onClick={() => setIsTextExpanded(!isTextExpanded)}
              className="text-blue-600 hover:text-blue-800 font-medium text-sm mt-1"
            >
              {isTextExpanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>
      )}

      {/* Metrics display */}
      {metrics.length > 0 && (
        <div className="mb-3 p-3 bg-blue-50 rounded-lg">
          <div className="flex flex-wrap gap-2">
            {metrics.map((metric, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
              >
                {metric}
              </span>
            ))}
          </div>
        </div>
      )}
      
      {/* Photo (show if photoURL exists) */}
      {story.photoURL && (
        <div className="mb-3 flex justify-center">
          <div className="w-full max-w-2xl">
            <img
              src={story.photoURL}
              alt="Story photo"
              className="w-full h-auto rounded-lg object-contain"
              style={{ maxHeight: '600px' }}
            />
          </div>
        </div>
      )}

      {/* Video (show if playableURL exists) */}
      {story.playableURL && (
        <div className="mb-3 flex justify-center">
          <div className="w-full max-w-md sm:max-w-lg md:max-w-xl">
            <div className="relative bg-gray-900 rounded-lg overflow-hidden mx-auto" style={{ aspectRatio: '9/16', maxWidth: '500px' }}>
              <video
                src={story.playableURL}
                className="w-full h-full object-contain"
                controls
                preload="metadata"
                playsInline
                muted
                autoPlay
                poster={story.posterURL || story.photoURL || undefined}
                onLoadStart={() => {
                  console.log('[StoryCard] Video load started. URL:', story.playableURL);
                }}
                onLoadedMetadata={(e) => {
                  console.log('[StoryCard] Video metadata loaded');
                  // Auto-play the video (muted) when metadata is loaded
                  if (e.target.duration > 0) {
                    e.target.currentTime = 0.1;
                    // Try to auto-play muted
                    e.target.play().catch(error => {
                      console.log('[StoryCard] Auto-play failed (expected on mobile):', error);
                    });
                  }
                }}
                onSeeked={(e) => {
                  console.log('[StoryCard] Video seeked to poster frame');
                  // Hide loading overlay when poster frame is ready
                  const loadingOverlay = e.target.parentElement.querySelector('.loading-overlay');
                  if (loadingOverlay) {
                    loadingOverlay.style.display = 'none';
                  }
                }}
                onLoadedData={(e) => {
                  console.log('[StoryCard] Video loaded successfully');
                  // Hide loading overlay when video loads
                  const loadingOverlay = e.target.parentElement.querySelector('.loading-overlay');
                  if (loadingOverlay) {
                    loadingOverlay.style.display = 'none';
                  }
                }}
                onError={(e) => {
                  console.error('[StoryCard] Video error. URL:', story.playableURL);
                  console.error('[StoryCard] Error details:', e.target.error);
                  const loadingOverlay = e.target.parentElement.querySelector('.loading-overlay');
                  if (loadingOverlay) {
                    loadingOverlay.innerHTML = '<div class="text-white text-sm">Video could not be loaded</div>';
                  }
                }}
              />
              {/* Loading overlay */}
              <div 
                className="loading-overlay absolute inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 rounded-lg cursor-pointer"
                onClick={(e) => {
                  const video = e.target.closest('.relative').querySelector('video');
                  if (video) {
                    console.log('[StoryCard] Retrying video load:', story.playableURL);
                    video.load();
                    e.target.innerHTML = '<div class="text-white text-sm">Loading video...</div>';
                  }
                }}
              >
                <div className="text-white text-sm">Loading video...</div>
              </div>
              
              {/* Error overlay with retry */}
              <div className="error-overlay absolute inset-0 flex items-center justify-center bg-gray-800 bg-opacity-75 rounded-lg" style={{ display: 'none' }}>
                <div className="text-center text-white">
                  <p className="text-sm mb-2">Video could not be loaded</p>
                  <button 
                    onClick={(e) => {
                      const video = e.target.closest('.relative').querySelector('video');
                      const errorOverlay = e.target.closest('.error-overlay');
                      const loadingOverlay = e.target.closest('.relative').querySelector('.loading-overlay');
                      
                      if (video) {
                        errorOverlay.style.display = 'none';
                        loadingOverlay.style.display = 'flex';
                        video.load();
                      }
                    }}
                    className="px-4 py-2 bg-white text-black rounded-lg text-sm hover:bg-gray-100"
                  >
                    Retry
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Interaction buttons */}
      <div className="border-t border-gray-100 pt-3 mt-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            {/* Prayer button */}
            <button 
              onClick={handlePray}
              className={`flex items-center space-x-1 transition-colors ${
                userHasPrayed ? 'text-blue-600' : 'text-gray-600 hover:text-blue-600'
              }`}
            >
              <svg className="w-5 h-5" fill={userHasPrayed ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              <span className="text-sm font-medium">Pray</span>
              {prayerCount > 0 && (
                <span className="text-xs text-gray-500">({prayerCount})</span>
              )}
            </button>

            {/* Like button */}
            <button 
              onClick={handleLike}
              className={`flex items-center space-x-1 transition-colors ${
                userHasLiked ? 'text-red-600' : 'text-gray-600 hover:text-red-600'
              }`}
            >
              <svg className="w-5 h-5" fill={userHasLiked ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
              </svg>
              <span className="text-sm font-medium">Like</span>
              {likeCount > 0 && (
                <span className="text-xs text-gray-500">({likeCount})</span>
              )}
            </button>

            {/* Comment button */}
            <button className="flex items-center space-x-1 text-gray-600 hover:text-green-600 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span className="text-sm font-medium">Comment</span>
              {comments.length > 0 && (
                <span className="text-xs text-gray-500">({comments.length})</span>
              )}
            </button>
          </div>
        </div>

        {/* Comment input */}
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Write a comment..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !isSubmittingComment) {
                  handleComment();
                }
              }}
            />
            <button
              onClick={handleComment}
              disabled={isSubmittingComment || !commentText.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmittingComment ? 'Posting...' : 'Post'}
            </button>
          </div>
        </div>

        {/* Comments section */}
        {comments.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="space-y-2">
              {visibleComments.map((comment) => (
                <div key={comment.id} className="flex items-start space-x-2">
                  <div className="flex-shrink-0">
                    {comment.userPhotoURL ? (
                      <img
                        src={comment.userPhotoURL}
                        alt={comment.userName || 'User'}
                        className="h-6 w-6 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-6 w-6 rounded-full bg-gray-300 flex items-center justify-center text-xs font-bold text-white">
                        {getInitials(comment.userName)}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="bg-gray-50 rounded-lg px-3 py-2">
                      <div className="text-xs font-medium text-gray-900">{comment.userName || 'Anonymous'}</div>
                      <div className="text-sm text-gray-700">{comment.text}</div>
                    </div>
                  </div>
                </div>
              ))}
              
              {comments.length > 3 && (
                <button
                  onClick={() => setShowAllComments(!showAllComments)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  {showAllComments ? 'Show less' : `Show ${comments.length - 3} more comments`}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoryCard; 